import React from 'react';
import { Building2, Star, MapPin, Clock, Check } from 'lucide-react';
import { packages } from '../data/mockData';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const SpecialOffers = ({ onOpenBuilder }) => {
  return (
    <section id="packages" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-[#d4af37] font-medium tracking-wider">UMRAH PACKAGES</span>
          <h2 
            className="text-3xl md:text-4xl font-bold text-gray-900 mt-2"
            style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
          >
            Special Umrah Offers
          </h2>
          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
            Choose from our carefully curated Umrah packages or build your own custom package.
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => (
            <div key={pkg.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-gray-100">
              {/* Package Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.name}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-[#d4af37] text-black px-3 py-1 rounded-full text-sm font-bold">
                  {pkg.duration}
                </div>
              </div>

              {/* Package Content */}
              <div className="p-6">
                <h3 
                  className="text-xl font-bold text-gray-900 mb-3"
                  style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
                >
                  {pkg.name}
                </h3>

                {/* Makkah Info */}
                <div className="flex items-start gap-3 mb-3 bg-gray-50 p-3 rounded-lg">
                  <div className="w-10 h-10 bg-[#d4af37]/20 rounded flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#d4af37]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">MAKKAH</span>
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">{pkg.mecca.nights} Nights</span>
                    </div>
                    <p className="text-sm text-gray-600">{pkg.mecca.hotel}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {[...Array(pkg.mecca.stars)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-[#d4af37] text-[#d4af37]" />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({pkg.mecca.roomType})</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3 text-[#d4af37]" />
                      <span className="text-xs text-[#d4af37]">{pkg.mecca.distance}</span>
                    </div>
                  </div>
                </div>

                {/* Madinah Info */}
                <div className="flex items-start gap-3 mb-4 bg-gray-50 p-3 rounded-lg">
                  <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-800">MADINAH</span>
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded">{pkg.medina.nights} Nights</span>
                    </div>
                    <p className="text-sm text-gray-600">{pkg.medina.hotel}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {[...Array(pkg.medina.stars)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-green-500 text-green-500" />
                      ))}
                      <span className="text-xs text-gray-500 ml-1">({pkg.medina.roomType})</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3 text-green-600" />
                      <span className="text-xs text-green-600">{pkg.medina.distance}</span>
                    </div>
                  </div>
                </div>

                {/* Includes */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 mb-2">INCLUDES:</p>
                  <div className="flex flex-wrap gap-1">
                    {pkg.includes.map((item, idx) => (
                      <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-4 border-t pt-4">
                  <span className="text-gray-400 line-through text-sm">
                    £{pkg.originalPrice}
                  </span>
                  <span className="text-[#d4af37] text-2xl font-bold ml-2">
                    £{pkg.currentPrice}
                  </span>
                  <span className="text-gray-500 text-sm"> / person</span>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={onOpenBuilder}
                    className="flex-1 bg-[#d4af37] text-black hover:bg-[#c4a030] font-semibold"
                  >
                    BOOK NOW
                  </Button>
                  <Button
                    variant="outline"
                    className="border-gray-300 hover:bg-gray-100"
                  >
                    DETAILS
                  </Button>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <Progress 
                    value={(pkg.booked / pkg.total) * 100} 
                    className="h-2 bg-gray-200"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {pkg.booked}/{pkg.total} spots booked
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button
            onClick={onOpenBuilder}
            className="bg-black text-white hover:bg-gray-800 px-8 py-3 font-bold"
          >
            BUILD YOUR OWN UMRAH PACKAGE
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SpecialOffers;
